import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { timeAPI, tasksAPI } from '../services/api';
import { Button } from './ui/button';
import { Square, Play } from 'lucide-react';
import { toast } from 'sonner';

const ActiveTimerWidget = ({ compact = false }) => {
  const navigate = useNavigate();
  const [activeEntry, setActiveEntry] = useState(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [elapsed, setElapsed] = useState('00:00:00');
  const intervalRef = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const res = await timeAPI.getActive();
      const entry = res.data;
      if (entry && entry.check_in) {
        setActiveEntry(entry);
        // Fetch task title
        try {
          const taskRes = await tasksAPI.get(entry.task_id);
          setTaskTitle(taskRes.data?.title || 'Unknown task');
        } catch {
          setTaskTitle('Unknown task');
        }
      } else {
        setActiveEntry(null);
        setTaskTitle('');
      }
    } catch {
      setActiveEntry(null);
    }
  }, []);

  // Poll for active entry every 30s
  useEffect(() => {
    fetchActive();
    const poll = setInterval(fetchActive, 30000);
    return () => clearInterval(poll);
  }, [fetchActive]);

  // Live tick every second
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (activeEntry?.check_in) {
      const tick = () => {
        const start = new Date(activeEntry.check_in).getTime();
        const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
        const h = String(Math.floor(diff / 3600)).padStart(2, '0');
        const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
        const s = String(diff % 60).padStart(2, '0');
        setElapsed(`${h}:${m}:${s}`);
      };
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed('00:00:00');
    }

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [activeEntry]);

  const handleCheckOut = async (e) => {
    e.stopPropagation();
    try {
      await timeAPI.checkOut();
      toast.success('Checked out');
      setActiveEntry(null);
      setTaskTitle('');
    } catch {
      toast.error('Failed to check out');
    }
  };

  // Listen for custom events from TasksPage check-in/out
  useEffect(() => {
    const handler = () => fetchActive();
    window.addEventListener('timetracking-changed', handler);
    return () => window.removeEventListener('timetracking-changed', handler);
  }, [fetchActive]);

  if (!activeEntry) return null;

  if (compact) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate('/dashboard/tasks')}
        onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/tasks'); }}
        className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-xs hover:bg-green-100 transition-colors cursor-pointer"
        data-testid="active-timer-widget-compact"
      >
        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="font-mono font-semibold text-green-800">{elapsed}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCheckOut}
          className="h-5 w-5 p-0 ml-0.5 hover:bg-red-100"
          data-testid="timer-checkout-compact"
        >
          <Square className="h-3 w-3 text-red-500" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
      onClick={() => navigate('/dashboard/tasks')}
      data-testid="active-timer-widget"
    >
      <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-slate-600 truncate max-w-[120px]">{taskTitle}</span>
        <span className="font-mono font-bold text-sm text-green-800 tabular-nums">{elapsed}</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCheckOut}
        className="h-6 w-6 p-0 hover:bg-red-100 flex-shrink-0"
        data-testid="timer-checkout-btn"
      >
        <Square className="h-3.5 w-3.5 text-red-500" />
      </Button>
    </div>
  );
};

export default ActiveTimerWidget;
